import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { BrutalCard } from "@/components/ui/BrutalCard";
import { BrutalButton } from "@/components/ui/BrutalButton";
import { BrutalInput } from "@/components/ui/BrutalInput";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { ExternalLink, Check, X, Eye, ArrowLeft, Edit3 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "@/components/admin/AdminLayout";
import { BlogPreviewModal } from "@/components/admin/BlogPreviewModal";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";

export default function CreatorReview() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [submissions, setSubmissions] = useState<any[]>([]);
  const [selectedSubmission, setSelectedSubmission] = useState<any>(null);
  const [reviewNotes, setReviewNotes] = useState("");
  const [rejectionReason, setRejectionReason] = useState("");
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<string>("pending");
  const [showBlogPreview, setShowBlogPreview] = useState(false);
  const [previewSubmission, setPreviewSubmission] = useState<any>(null);

  useEffect(() => {
    loadSubmissions();
  }, [filter]);

  const loadSubmissions = async () => {
    try {
      let query = supabase
        .from("creator_submissions")
        .select("*")
        .order("submission_date", { ascending: false });

      if (filter !== "all") {
        query = query.eq("review_status", filter);
      }

      const { data, error } = await query;
      if (error) throw error;

      setSubmissions(data || []);
    } catch (error) {
      console.error("Error loading submissions:", error);
      toast.error(t("common.error"));
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (submissionId: string) => {
    try {
      const { data, error } = await supabase.functions.invoke('approve-creator-submission', {
        body: { submissionId, reviewNotes }
      });

      if (error) throw error;

      if (data.creditsAwarded) {
        toast.success(
          `${t("admin.creator.approved")} - €${data.rewardAmount} crediti Spotlight assegnati`,
          { duration: 4000 }
        );
      } else {
        toast.warning(
          `${t("admin.creator.approvedNoCredits")} - Limite annuale raggiunto (${data.currentCount}/${data.maxCount})`,
          { duration: 5000 }
        );
      }

      setSelectedSubmission(null);
      setReviewNotes("");
      loadSubmissions();
    } catch (error: any) {
      console.error("Error approving submission:", error);
      toast.error(error.message);
    }
  };

  const handleReject = async (submissionId: string) => {
    if (!rejectionReason.trim()) {
      toast.error(t("admin.creator.rejectionReasonRequired"));
      return;
    }

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { error } = await supabase
        .from("creator_submissions")
        .update({
          review_status: "rejected",
          review_date: new Date().toISOString(),
          reviewed_by: user.id,
          review_notes: reviewNotes,
          rejection_reason: rejectionReason
        })
        .eq("id", submissionId);

      if (error) throw error;

      toast.success(t("admin.creator.rejected"));
      setSelectedSubmission(null);
      setReviewNotes("");
      setRejectionReason("");
      loadSubmissions();
    } catch (error: any) {
      console.error("Error rejecting submission:", error);
      toast.error(error.message);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusColors: Record<string, string> = {
      pending: "bg-warning text-warning-foreground",
      in_review: "bg-primary text-primary-foreground",
      approved: "bg-success text-success-foreground",
      rejected: "bg-destructive text-destructive-foreground",
      revision_needed: "bg-warning text-warning-foreground"
    };

    return (
      <Badge className={statusColors[status] || ""}>
        {t(`creator.status.${status}`)}
      </Badge>
    );
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="p-6">{t("common.loading")}...</div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-8">
        <Breadcrumb className="mb-6">
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink href="/admin/dashboard">Dashboard</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>{t("admin.creator.title")}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        <div className="flex items-center gap-4 mb-8">
          <button
            onClick={() => navigate("/admin/dashboard")}
            className="p-2 hover:bg-accent transition-colors rounded-lg"
            aria-label={t("common.back")}
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="font-heading text-4xl font-bold flex items-center gap-3">
              <Edit3 className="w-10 h-10 text-pink-600" />
              {t("admin.creator.title")}
            </h1>
            <p className="text-muted-foreground mt-1">{t("admin.creator.subtitle")}</p>
          </div>
        </div>

      {/* Filters */}
      <div className="flex gap-2 mb-6">
        {["all", "pending", "in_review", "approved", "rejected"].map((status) => (
          <BrutalButton
            key={status}
            variant={filter === status ? "primary" : "outline"}
            size="sm"
            onClick={() => setFilter(status)}
          >
            {t(`admin.creator.filter.${status}`)}
          </BrutalButton>
        ))}
      </div>

      {/* Submissions List */}
      <div className="grid gap-4">
        {submissions.map((submission) => (
          <BrutalCard key={submission.id} className="p-6">
            <div className="flex justify-between items-start mb-4">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h3 className="font-heading text-xl">{submission.title}</h3>
                  {getStatusBadge(submission.review_status)}
                  <Badge variant="outline">{submission.content_type}</Badge>
                  {submission.review_status === "approved" && (
                    <Badge variant={submission.credits_awarded ? "default" : "secondary"}>
                      {submission.credits_awarded 
                        ? `✅ €${parseFloat(submission.reward_amount || 0).toFixed(2)} Spotlight`
                        : `⚠️ ${t("admin.creator.approvedNoCredits")}`
                      }
                    </Badge>
                  )}
                </div>
                <p className="text-sm text-muted-foreground mb-2">
                  {t("admin.creator.submittedOn")}: {format(new Date(submission.submission_date), "dd/MM/yyyy HH:mm")}
                </p>
                {submission.content_description && (
                  <p className="text-sm mb-3">{submission.content_description}</p>
                )}
                
                {submission.content_type === 'blog_article' && (
                  <div className="mt-3 space-y-1 text-sm bg-muted/30 p-3 border-l-[3px] border-primary">
                    {submission.meta_description && (
                      <p><strong>{t("admin.creator.blogLabels.meta")}:</strong> {submission.meta_description}</p>
                    )}
                    {submission.word_count && (
                      <p><strong>{t("admin.creator.blogLabels.words")}:</strong> {submission.word_count}</p>
                    )}
                    {submission.tags && submission.tags.length > 0 && (
                      <p><strong>{t("admin.creator.blogLabels.tags")}:</strong> {submission.tags.join(", ")}</p>
                    )}
                    {submission.featured_image_url && (
                      <p>
                        <a href={submission.featured_image_url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                          📸 {t("admin.creator.blogLabels.viewCoverImage")}
                        </a>
                      </p>
                    )}
                  </div>
                )}

                <div className="flex gap-3 mt-3">
                  <a
                    href={submission.content_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline flex items-center gap-1 text-sm"
                  >
                    {t("admin.creator.viewContent")} <ExternalLink className="w-3 h-3" />
                  </a>
                  
                  {submission.content_type === 'blog_article' && submission.content_html && (
                    <button
                      onClick={() => {
                        setPreviewSubmission(submission);
                        setShowBlogPreview(true);
                      }}
                      className="text-primary hover:underline flex items-center gap-1 text-sm"
                    >
                      <Eye className="w-3 h-3" />
                      {t("admin.creator.viewBlogPreview")}
                    </button>
                  )}
                </div>
              </div>

              {submission.screenshot_url && (
                <img
                  src={submission.screenshot_url}
                  alt="Screenshot"
                  className="w-32 h-32 object-cover border-[2px] border-dark ml-4"
                />
              )}
            </div>

            {submission.review_status === "pending" && (
              <div className="border-t-[2px] border-dark pt-4 mt-4">
                <div className="mb-4">
                  <label className="font-bold mb-2 block">{t("admin.creator.reviewNotes")}</label>
                  <textarea
                    className="brutal-input w-full h-20"
                    value={selectedSubmission?.id === submission.id ? reviewNotes : ""}
                    onChange={(e) => {
                      setSelectedSubmission(submission);
                      setReviewNotes(e.target.value);
                    }}
                    placeholder={t("admin.creator.notesPlaceholder")}
                  />
                </div>

                <div className="mb-4">
                  <label className="font-bold mb-2 block">{t("admin.creator.rejectionReason")}</label>
                  <BrutalInput
                    value={selectedSubmission?.id === submission.id ? rejectionReason : ""}
                    onChange={(e) => {
                      setSelectedSubmission(submission);
                      setRejectionReason(e.target.value);
                    }}
                    placeholder={t("admin.creator.rejectionPlaceholder")}
                  />
                </div>

                <div className="flex gap-3">
                  <BrutalButton
                    variant="primary"
                    onClick={() => handleApprove(submission.id)}
                    className="flex-1"
                  >
                    <Check className="w-4 h-4 mr-2" />
                    {t("admin.creator.approve")} (€{parseFloat(submission.reward_amount).toFixed(2)})
                  </BrutalButton>
                  <BrutalButton
                    variant="outline"
                    onClick={() => handleReject(submission.id)}
                    className="flex-1"
                  >
                    <X className="w-4 h-4 mr-2" />
                    {t("admin.creator.reject")}
                  </BrutalButton>
                </div>
              </div>
            )}

            {submission.review_status !== "pending" && submission.review_notes && (
              <div className="border-t-[2px] border-dark pt-4 mt-4">
                <p className="text-sm font-bold mb-1">{t("admin.creator.adminNotes")}:</p>
                <p className="text-sm text-muted-foreground">{submission.review_notes}</p>
                {submission.rejection_reason && (
                  <>
                    <p className="text-sm font-bold mt-2 mb-1">{t("admin.creator.rejectionReason")}:</p>
                    <p className="text-sm text-destructive">{submission.rejection_reason}</p>
                  </>
                )}
              </div>
            )}
          </BrutalCard>
        ))}

        {submissions.length === 0 && (
          <BrutalCard className="p-12 text-center">
            <p className="text-muted-foreground">{t("admin.creator.noSubmissions")}</p>
          </BrutalCard>
        )}
      </div>

      <BlogPreviewModal
        isOpen={showBlogPreview}
        onClose={() => {
          setShowBlogPreview(false);
          setPreviewSubmission(null);
        }}
        submission={previewSubmission}
      />
      </div>
    </AdminLayout>
  );
}
